import React, { useEffect, useState } from 'react'
import { useTheme } from '../context/ThemeContext'
import PdfGenerationStudio from '../components/PdfGenerationStudio'
import ThemeModeSlider from '../components/ThemeModeSlider'
import UserManagement from '../components/UserManagement'
import useRolePermissions from '../hooks/useRolePermissions'
import {
  emitMobileBlockSettingUpdate
} from '../utils/mobileViewPolicy'
import { settingsService } from '../services/api'

export default function Settings() {
  const { theme, setTheme, themeOptions } = useTheme()
  const { isAdmin, isSuperAdmin, role, canManageUsers } = useRolePermissions()

  // Debug: Log role permissions
  const [activeTab, setActiveTab] = useState('appearance')
  const [isMobileBlockEnabled, setIsMobileBlockEnabled] = useState(false)
  const [mobileBlockSource, setMobileBlockSource] = useState('database')
  const [isPublicFormLimitationEnabled, setIsPublicFormLimitationEnabled] = useState(true)
  const [publicFormLimitationSource, setPublicFormLimitationSource] = useState('database')

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const [mobileBlockResponse, publicFormLimitationResponse] = await Promise.all([
          settingsService.getMobileBlockView(),
          settingsService.getPublicFormLimitation()
        ])

        setIsMobileBlockEnabled(Boolean(mobileBlockResponse?.data?.data?.enabled))
        setMobileBlockSource(String(mobileBlockResponse?.data?.data?.source || 'database'))

        setIsPublicFormLimitationEnabled(Boolean(publicFormLimitationResponse?.data?.data?.enabled))
        setPublicFormLimitationSource(String(publicFormLimitationResponse?.data?.data?.source || 'database'))
      } catch (error) {
        console.error('Failed to load settings:', error)
      }
    }

    loadSettings()
  }, [])

  const settingsTabs = [
    { id: 'appearance', label: 'Appearance' },
    { id: 'template-preview', label: 'Template Preview' },
    ...(canManageUsers ? [{ id: 'user-management', label: 'Create User Account' }] : [])
  ]

  const handleMobileBlockToggle = async () => {
    if (!isAdmin) return

    const currentValue = isMobileBlockEnabled
    const nextValue = !currentValue

    setIsMobileBlockEnabled(nextValue)
    try {
      await settingsService.updateMobileBlockView(nextValue)
      setMobileBlockSource('database')
      emitMobileBlockSettingUpdate(nextValue)
    } catch (error) {
      console.error('Failed to update mobile block setting:', error)
      setIsMobileBlockEnabled(currentValue)
    }
  }

  const handlePublicFormLimitationToggle = async () => {
    if (!isAdmin) return

    const currentValue = isPublicFormLimitationEnabled
    const nextValue = !currentValue

    setIsPublicFormLimitationEnabled(nextValue)
    try {
      await settingsService.updatePublicFormLimitation(nextValue)
      setPublicFormLimitationSource('database')
    } catch (error) {
      console.error('Failed to update public form limitation setting:', error)
      setIsPublicFormLimitationEnabled(currentValue)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-slate-100">Settings</h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-slate-400">Appearance controls and PDF template preview settings for OMS.</p>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-2 shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Settings sections">
          {settingsTabs.map((tab) => {
            const isActive = activeTab === tab.id
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tab.id)}
                className={[
                  'rounded-xl px-4 py-2 text-sm font-semibold transition border',
                  isActive
                    ? 'border-slate-900 bg-slate-900 text-white shadow-sm dark:border-sky-400 dark:bg-sky-500 dark:text-white'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800'
                ].join(' ')}
              >
                {tab.label}
              </button>
            )
          })}
        </div>
      </div>

      {activeTab === 'appearance' && (
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Display preferences</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-400">
              Manage theme mode and prepare for additional personalization options.
            </p>
          </div>

          <div className="space-y-3">
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Theme Mode</p>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Light and dark mode are available across the app.</p>
                </div>
                <ThemeModeSlider
                  isDark={theme === themeOptions.DARK}
                  onToggle={() => setTheme(theme === themeOptions.DARK ? themeOptions.LIGHT : themeOptions.DARK)}
                />
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Mobile Block View</p>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Block non-dashboard pages on mobile devices. Source: <span className="font-medium">{mobileBlockSource === 'env-default' ? 'Environment default' : 'Database'}</span>
                  </p>
                  {!isAdmin && (
                    <p className="mt-1 text-xs font-medium text-amber-600 dark:text-amber-400">Only Admin can change this setting.</p>
                  )}
                </div>

                <label className={`relative inline-flex h-7 w-14 shrink-0 ${isAdmin ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                  <input
                    type="checkbox"
                    checked={isMobileBlockEnabled}
                    onChange={handleMobileBlockToggle}
                    disabled={!isAdmin}
                    className="peer sr-only"
                    aria-label="Toggle mobile block view"
                  />
                  <span className="absolute inset-0 rounded-full bg-slate-300 transition-colors duration-200 peer-checked:bg-sky-500 dark:bg-slate-700" />
                  <span className="absolute left-0.5 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full bg-white shadow-sm transition-transform duration-200 peer-checked:translate-x-7 dark:bg-slate-100" />
                </label>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Public Form Limitation</p>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Enable anti-abuse limitations for public attendance and expense forms. Source: <span className="font-medium">{publicFormLimitationSource === 'env-default' ? 'Environment default' : 'Database'}</span>
                  </p>
                  {!isAdmin && (
                    <p className="mt-1 text-xs font-medium text-amber-600 dark:text-amber-400">Only Admin can change this setting.</p>
                  )}
                </div>

                <label className={`relative inline-flex h-7 w-14 shrink-0 ${isAdmin ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                  <input
                    type="checkbox"
                    checked={isPublicFormLimitationEnabled}
                    onChange={handlePublicFormLimitationToggle}
                    disabled={!isAdmin}
                    className="peer sr-only"
                    aria-label="Toggle public form limitation"
                  />
                  <span className="absolute inset-0 rounded-full bg-slate-300 transition-colors duration-200 peer-checked:bg-sky-500 dark:bg-slate-700" />
                  <span className="absolute left-0.5 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full bg-white shadow-sm transition-transform duration-200 peer-checked:translate-x-7 dark:bg-slate-100" />
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'template-preview' && (
        <div className="space-y-4">
          <div className="rounded-3xl border border-amber-100 bg-gradient-to-r from-amber-50 via-orange-50 to-stone-100 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800">
            <span className="inline-flex rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
              Template Preview
            </span>
            <h2 className="mt-4 text-2xl font-bold text-slate-900 dark:text-slate-100">Template preview by page type</h2>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-600 dark:text-slate-300">
              Use tab view selection to preview the document layout for Quotation, Purchase Order, and Invoice in one place.
            </p>
          </div>

          <PdfGenerationStudio />
        </div>
      )}

      {activeTab === 'user-management' && canManageUsers && (
        <div className="space-y-4">
          <UserManagement />
        </div>
      )}
    </div>
  )
}